import { useFocusEffect, useGlobalSearchParams, useNavigation } from 'expo-router';
import { useCallback, useEffect } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import Animated, { useAnimatedRef } from 'react-native-reanimated';

import { useGetNonProfitInsights } from '@/api/hooks/nonProfitOrgHooks';
import DotsLoader from '@/components/DotsLoader';
import { useScrollProps } from '@/components/navigation/TopTabsReanimated';
import InsightsComponent from '@/components/npo/details/insights/InsightsComponent';
import { useAuth } from '@/contexts/authContext';

const NPOInsightsScreen = () => {
  const { state } = useAuth();
  const { npoId } = useGlobalSearchParams<{
    npoId: string;
  }>();

  const { data, refetch, isPending } = useGetNonProfitInsights({
    id: npoId ?? state.signUpData?.loginEmail?.npoId
  });
  const { scrollEnabled, scrollTopHeader, scrollHandler } = useScrollProps();

  const animatedRef = useAnimatedRef<Animated.ScrollView>();
  const navigation = useNavigation<any>();
  useEffect(() => {
    const unsubscribe = navigation.addListener('tabPress', () => {
      scrollTopHeader();
      animatedRef.current?.scrollTo({
        y: 0,
        animated: false
      });
    });

    return unsubscribe;
  }, [animatedRef, navigation, scrollTopHeader]);

  // FocusEffect to refetch data when we update something
  useFocusEffect(
    useCallback(() => {
      if (npoId) refetch();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [npoId])
  );

  return (
    <Animated.ScrollView
      ref={animatedRef}
      onScroll={scrollHandler}
      scrollEnabled={scrollEnabled}
      showsVerticalScrollIndicator={false}
      bounces={false}
      style={styles.scrollViewContainer}>
      {isPending ? (
        <View style={styles.loadingContainer}>
          <DotsLoader />
        </View>
      ) : (
        <InsightsComponent canEdit={true} data={data} />
      )}
    </Animated.ScrollView>
  );
};

export default NPOInsightsScreen;

const styles = StyleSheet.create({
  scrollViewContainer: {
    paddingHorizontal: Platform.OS !== 'web' ? 18 : 0
  },

  loadingContainer: {
    paddingTop: 30,
    alignItems: 'center'
  }
});
